// Fixed lists the whole product shares.

export interface CurrencyInfo {
  code: string;
  name: string;
  /** Symbol shown before amounts; currencies without one are written with their code ("CHF 72,000"). */
  symbol: string | null;
}

/** Currencies with a daily reference rate from the European Central Bank, plus the euro. */
export const CURRENCIES: CurrencyInfo[] = [
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'Pound sterling', symbol: '£' },
  { code: 'USD', name: 'US dollar', symbol: '$' },
  { code: 'CHF', name: 'Swiss franc', symbol: null },
  { code: 'DKK', name: 'Danish krone', symbol: null },
  { code: 'NOK', name: 'Norwegian krone', symbol: null },
  { code: 'SEK', name: 'Swedish krona', symbol: null },
  { code: 'PLN', name: 'Polish złoty', symbol: null },
  { code: 'CZK', name: 'Czech koruna', symbol: null },
  { code: 'HUF', name: 'Hungarian forint', symbol: null },
  { code: 'RON', name: 'Romanian leu', symbol: null },
  { code: 'ISK', name: 'Icelandic króna', symbol: null },
  { code: 'TRY', name: 'Turkish lira', symbol: null },
  { code: 'CAD', name: 'Canadian dollar', symbol: null },
  { code: 'AUD', name: 'Australian dollar', symbol: null },
  { code: 'NZD', name: 'New Zealand dollar', symbol: null },
  { code: 'JPY', name: 'Japanese yen', symbol: null },
  { code: 'CNY', name: 'Chinese yuan', symbol: null },
  { code: 'HKD', name: 'Hong Kong dollar', symbol: null },
  { code: 'SGD', name: 'Singapore dollar', symbol: null },
  { code: 'KRW', name: 'South Korean won', symbol: null },
  { code: 'INR', name: 'Indian rupee', symbol: null },
  { code: 'IDR', name: 'Indonesian rupiah', symbol: null },
  { code: 'MYR', name: 'Malaysian ringgit', symbol: null },
  { code: 'PHP', name: 'Philippine peso', symbol: null },
  { code: 'THB', name: 'Thai baht', symbol: null },
  { code: 'ILS', name: 'Israeli shekel', symbol: null },
  { code: 'MXN', name: 'Mexican peso', symbol: null },
  { code: 'BRL', name: 'Brazilian real', symbol: null },
  { code: 'ZAR', name: 'South African rand', symbol: null },
];

export const CURRENCY_CODES = CURRENCIES.map((c) => c.code);

/** Where a record came from. Free text is allowed after the kind ("Referral · Oliver Hartley"). */
export const SOURCE_KINDS = [
  'Referral',
  'Event',
  'Website enquiry',
  'Shared inbox',
  'Forwarded email',
  'AI Finder',
  'LinkedIn outreach',
  'Existing relationship',
  'Added manually',
  'Other',
] as const;

export const WON_REASONS = ['Price', 'Relevant experience', 'Relationship', 'Timeline fit', 'Technical approach', 'Other'];
export const LOST_REASONS = ['Budget', 'Chose a competitor', 'Went in-house', 'Timing', 'No decision', 'Went quiet', 'Other'];

export const STAGE_COLORS = ['lead', 'qual', 'prop', 'nego', 'info', 'accent', 'warning', 'won', 'lost'] as const;

/** Days a deleted item stays in the bin before it is removed for good. */
export const BIN_DAYS = 30;

export const REPLY_WINDOW = { min: 7, max: 90, default: 30 };

/** Kinds of entries in the team activity log, used by the weekly view. */
export const ACTIVITY_KINDS = [
  'email_sent',
  'email_received',
  'call_logged',
  'note_added',
  'comment_added',
  'meeting_held',
  'followup_done',
  'deal_created',
  'deal_moved',
  'deal_won',
  'deal_lost',
  'person_created',
  'company_created',
  'finder_approved',
] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

/** Email addresses one person can list, merges included. */
export const PERSON_EMAILS_MAX = 20;

/** Passwords: long enough to resist guessing, with no other rules. Checked in the browser and again on the server. */
export const PASSWORD_MIN = 10;
export const PASSWORD_MAX = 200;

/** The passwords the sample sign-in accounts start with. They are published in the README, so nobody can choose them. */
export const SAMPLE_PASSWORDS = { admin: 'Clientbook-admin-2026', member: 'Clientbook-member-2026' } as const;

export function passwordProblem(password: string): string | null {
  if (password.length < PASSWORD_MIN) return `Use at least ${PASSWORD_MIN} characters.`;
  if (password.length > PASSWORD_MAX) return `Use at most ${PASSWORD_MAX} characters.`;
  if (Object.values<string>(SAMPLE_PASSWORDS).includes(password)) return 'That is the published sample password. Choose one of your own.';
  return null;
}
